// «+ Nuevo»: alta de producto, kit o servicio en el catálogo de la cadena (retail.product.create).
import { useState } from 'react';
import { Controller, useForm, type Control, type UseFormRegisterReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { SKU_MAX_LENGTH } from '@fiado/type-kit/bin/common/constants/InputPatterns';
import { toast } from 'sonner';
import { Input } from '@/kit';
import { CreateDrawer, NewButton, type CreateDrawerTab } from '@/shared/patterns/CreateDrawer';
import { useIdempotentAttempt } from '@/hooks/useIdempotentAttempt';
import { attemptForScope } from '@/lib/idempotentAttempt';
import {
    useCatalogProducts,
    useCreateCatalogItem,
    useRegisterSkuPrice,
    type NewCatalogItem,
} from '../../api/useCatalogWrites';
import {
    TRACKING_MODE_OPTIONS,
    emptyCreateForm,
    kitFormSchema,
    productFormSchema,
    serviceFormSchema,
    toCreateKitRequest,
    toCreateProductRequest,
    toCreateServiceRequest,
    toInitialPriceRequest,
    type CatalogItemKind,
    type KitForm,
    type ProductForm,
    type ServiceForm,
} from '../../lib/catalogForms';
import { useInventoryAbilities } from '../../lib/inventoryPermissions';
import { applyWriteError } from '../../lib/inventoryWriteErrors';
import {
    IntegrationLockNotice,
    useIntegrationLock,
} from '../../../integration/components/IntegrationLock';
import { FinancierChecks, FormSection, FormSelect } from './FormBits';
import {
    KitComponentsField,
    componentOptions,
    type KitComponentValues,
} from './KitComponentsField';
import {
    ProductFields,
    SaleSwitches,
    TextController,
    type ProductFieldValues,
} from './ProductFields';

const CREATED_TOAST: Record<CatalogItemKind, string> = {
    PRODUCT: 'Producto dado de alta',
    KIT: 'Kit dado de alta',
    SERVICE: 'Servicio dado de alta',
};

const SKU_HINT = 'Letras, números, guiones y espacios entre palabras. No se puede cambiar después.';

export const PRICE_NOT_REGISTERED =
    'Se dio de alta, pero no se registró el precio; regístralo en la pestaña Precio.';

interface SubmitState {
    /** `onDuplicateSku` marca el campo SKU cuando el silo responde DUPLICATE_SKU. */
    submit: (
        item: NewCatalogItem,
        basePrice: string,
        onDuplicateSku: (message: string) => void,
    ) => Promise<void>;
    isPending: boolean;
    error: string | null;
    /** SKU ya creado al que le falta el precio: el reintento solo registra el precio. */
    pricePendingSku: string | null;
    clearError: () => void;
}

/** Da de alta con su llave y registra el precio de contado; si el precio falla, no repite el alta. */
function useCreateSubmit(onCreated: (sku: string) => void, onDone: () => void): SubmitState {
    const create = useCreateCatalogItem();
    const registerPrice = useRegisterSkuPrice();
    const attempt = useIdempotentAttempt('catalog:create');
    const [error, setError] = useState<string | null>(null);
    const [pricePendingSku, setPricePendingSku] = useState<string | null>(null);
    const submit: SubmitState['submit'] = async (item, basePrice, onDuplicateSku) => {
        setError(null);
        const sku = pricePendingSku ?? item.body.sku;
        if (pricePendingSku === null) {
            try {
                await attempt.run(item, (key) => create.mutateAsync({ item, key }));
            } catch (failure) {
                const mapped = applyWriteError(failure, attempt, item);
                if (mapped.code === 'DUPLICATE_SKU') onDuplicateSku(mapped.message);
                setError(mapped.message);
                return;
            }
            onCreated(sku);
        }
        const price = toInitialPriceRequest(basePrice);
        const priceAttempt = attemptForScope(`price:${sku}`);
        try {
            await priceAttempt.run(price, (key) =>
                registerPrice.mutateAsync({ sku, body: price, key }),
            );
        } catch (failure) {
            applyWriteError(failure, priceAttempt, price);
            setPricePendingSku(sku);
            setError(PRICE_NOT_REGISTERED);
            return;
        }
        toast.success(pricePendingSku === null ? CREATED_TOAST[item.kind] : 'Precio registrado', {
            description: sku,
        });
        onDone();
    };
    return {
        submit,
        isPending: create.isPending || registerPrice.isPending,
        error,
        pricePendingSku,
        clearError: () => {
            setError(null);
        },
    };
}

/** Marca el SKU repetido en el campo del formulario. */
function markSku(setError: (name: 'sku', error: { message: string }) => void) {
    return (message: string) => {
        setError('sku', { message });
    };
}

const FORM_CLASS = 'flex flex-col gap-5';

/** Precio de contado del alta; los cambios de precio van en la pestaña Precio. */
function InitialPriceSection({
    registration,
    error,
}: {
    registration: UseFormRegisterReturn<'basePrice'>;
    error?: string;
}) {
    return (
        <FormSection title="Precio">
            <Input
                label="Precio de contado"
                required
                inputMode="decimal"
                placeholder="0.00"
                hint="Se registra junto con el alta. Los cambios de precio van en la pestaña Precio."
                error={error}
                {...registration}
            />
        </FormSection>
    );
}

interface NewFormProps {
    formId: string;
    state: SubmitState;
}

function NewProductForm({ formId, state }: NewFormProps) {
    const form = useForm<ProductForm>({
        resolver: zodResolver(productFormSchema),
        defaultValues: emptyCreateForm('PRODUCT'),
    });
    // Alta y edición comparten los campos de producto con los mismos nombres.
    const shared = form.control as unknown as Control<ProductFieldValues>;
    return (
        <form
            id={formId}
            noValidate
            autoComplete="off"
            className={FORM_CLASS}
            onSubmit={(event) => {
                void form.handleSubmit((values) =>
                    state.submit(
                        { kind: 'PRODUCT', body: toCreateProductRequest(values) },
                        values.basePrice,
                        markSku(form.setError),
                    ),
                )(event);
            }}
        >
            <ProductFields
                control={shared}
                leading={
                    <>
                        <div className="grid grid-cols-2 gap-3">
                            <Input
                                label="SKU"
                                required
                                hint={SKU_HINT}
                                maxLength={SKU_MAX_LENGTH}
                                error={form.formState.errors.sku?.message}
                                {...form.register('sku')}
                            />
                            <Controller
                                control={form.control}
                                name="trackingMode"
                                render={({ field, fieldState }) => (
                                    <FormSelect
                                        label="Rastreo"
                                        required
                                        value={field.value}
                                        onChange={field.onChange}
                                        options={TRACKING_MODE_OPTIONS}
                                        placeholder="IMEI, serie o cantidad"
                                        error={fieldState.error?.message}
                                    />
                                )}
                            />
                        </div>
                        <p className="-mt-1 text-[10.5px] text-muted-foreground">
                            El modo de rastreo no se puede cambiar después del alta.
                        </p>
                    </>
                }
            />
            <InitialPriceSection
                registration={form.register('basePrice')}
                error={form.formState.errors.basePrice?.message}
            />
        </form>
    );
}

function NewKitForm({ formId, state, retailerId }: NewFormProps & { retailerId: string | null }) {
    const form = useForm<KitForm>({
        resolver: zodResolver(kitFormSchema),
        defaultValues: emptyCreateForm('KIT'),
    });
    const products = useCatalogProducts(retailerId, true);
    const shared = form.control as unknown as Control<ProductFieldValues>;
    return (
        <form
            id={formId}
            noValidate
            autoComplete="off"
            className={FORM_CLASS}
            onSubmit={(event) => {
                void form.handleSubmit((values) =>
                    state.submit(
                        { kind: 'KIT', body: toCreateKitRequest(values) },
                        values.basePrice,
                        markSku(form.setError),
                    ),
                )(event);
            }}
        >
            <FormSection title="Kit">
                <div className="grid grid-cols-2 gap-3">
                    <Input
                        label="SKU"
                        required
                        hint={SKU_HINT}
                        maxLength={SKU_MAX_LENGTH}
                        error={form.formState.errors.sku?.message}
                        {...form.register('sku')}
                    />
                    <TextController
                        control={shared}
                        name="externalId"
                        label="ID externo"
                        hint="El SKU en el sistema del retailer (Zentral)."
                    />
                </div>
                <TextController
                    control={shared}
                    name="description"
                    label="Nombre del kit"
                    placeholder="Galaxy A15 + funda + mica"
                />
                <TextController
                    control={shared}
                    name="imageUrl"
                    label="URL de imagen"
                    placeholder="https://"
                />
            </FormSection>
            <FormSection title="Componentes">
                <KitComponentsField
                    control={form.control as unknown as Control<KitComponentValues>}
                    options={componentOptions(products.data ?? [])}
                    isLoading={products.isPending}
                    error={form.formState.errors.components?.message}
                />
                {products.isError ? (
                    <p className="text-[11px] text-danger">
                        No se pudo cargar el catálogo para elegir componentes.
                    </p>
                ) : null}
            </FormSection>
            <FormSection title="Venta">
                <SaleSwitches control={shared} />
                <Controller
                    control={form.control}
                    name="eligibleFinanciers"
                    render={({ field }) => (
                        <FinancierChecks value={field.value} onChange={field.onChange} />
                    )}
                />
            </FormSection>
            <InitialPriceSection
                registration={form.register('basePrice')}
                error={form.formState.errors.basePrice?.message}
            />
        </form>
    );
}

function NewServiceForm({ formId, state }: NewFormProps) {
    const form = useForm<ServiceForm>({
        resolver: zodResolver(serviceFormSchema),
        defaultValues: emptyCreateForm('SERVICE'),
    });
    const shared = form.control as unknown as Control<ProductFieldValues>;
    return (
        <form
            id={formId}
            noValidate
            autoComplete="off"
            className={FORM_CLASS}
            onSubmit={(event) => {
                void form.handleSubmit((values) =>
                    state.submit(
                        { kind: 'SERVICE', body: toCreateServiceRequest(values) },
                        values.basePrice,
                        markSku(form.setError),
                    ),
                )(event);
            }}
        >
            <FormSection title="Servicio">
                <p className="text-[11px] text-muted-foreground">
                    Un servicio (tiempo aire, por ejemplo) se vende sin inventario, piezas ni
                    apartado.
                </p>
                <div className="grid grid-cols-2 gap-3">
                    <Input
                        label="SKU"
                        required
                        hint={SKU_HINT}
                        maxLength={SKU_MAX_LENGTH}
                        error={form.formState.errors.sku?.message}
                        {...form.register('sku')}
                    />
                    <TextController
                        control={shared}
                        name="externalId"
                        label="ID externo"
                        hint="El SKU en el sistema del retailer (Zentral)."
                    />
                </div>
                <TextController
                    control={shared}
                    name="description"
                    label="Nombre del servicio"
                    placeholder="Tiempo aire $100"
                    required
                />
            </FormSection>
            <FormSection title="Venta">
                <SaleSwitches control={shared} />
            </FormSection>
            <InitialPriceSection
                registration={form.register('basePrice')}
                error={form.formState.errors.basePrice?.message}
            />
        </form>
    );
}

function NewItemEditor({
    onClose,
    retailerId,
    onCreated,
}: {
    onClose: () => void;
    retailerId: string | null;
    onCreated: (sku: string) => void;
}) {
    const { locked } = useIntegrationLock();
    // Con la cadena integrada los productos llegan de Zentral: solo se dan de alta kits y servicios.
    const [kind, setKind] = useState<CatalogItemKind>(locked ? 'KIT' : 'PRODUCT');
    const state = useCreateSubmit(onCreated, onClose);
    const productTab: CreateDrawerTab<CatalogItemKind> = {
        key: 'PRODUCT',
        label: 'Producto',
        render: (formId) => <NewProductForm formId={formId} state={state} />,
    };
    return (
        <CreateDrawer<CatalogItemKind>
            title="Nuevo en el catálogo"
            tabs={[
                ...(locked ? [] : [productTab]),
                {
                    key: 'KIT',
                    label: 'Kit',
                    render: (formId) => (
                        <>
                            <IntegrationLockNotice message="lockedNewProduct" />
                            <NewKitForm formId={formId} state={state} retailerId={retailerId} />
                        </>
                    ),
                },
                {
                    key: 'SERVICE',
                    label: 'Servicio',
                    render: (formId) => (
                        <>
                            <IntegrationLockNotice message="lockedNewProduct" />
                            <NewServiceForm formId={formId} state={state} />
                        </>
                    ),
                },
            ]}
            activeTab={kind}
            onTabChange={(key) => {
                // Con el SKU ya creado solo falta su precio: no se cambia de tipo.
                if (state.pricePendingSku !== null) return;
                state.clearError();
                setKind(key);
            }}
            submitLabel={state.pricePendingSku === null ? 'Dar de alta' : 'Registrar precio'}
            isPending={state.isPending}
            error={state.error}
            onClose={onClose}
        />
    );
}

/** Cada apertura monta un editor nuevo (`openCount`): el alta arranca vacía y sin el error anterior. */
export function NewItemDrawer({
    isOpen,
    openCount = 0,
    onClose,
    retailerId,
    onCreated,
}: {
    isOpen: boolean;
    openCount?: number;
    onClose: () => void;
    retailerId: string | null;
    onCreated: (sku: string) => void;
}) {
    if (!isOpen) return null;
    return (
        <NewItemEditor
            key={openCount}
            onClose={onClose}
            retailerId={retailerId}
            onCreated={onCreated}
        />
    );
}

/** Sin cadena o sin permiso de precio, por qué el alta queda deshabilitada. */
export function newItemBlockedReason(
    retailerId: string | null,
    canUpdateProduct: boolean,
): string | undefined {
    if (retailerId === null) return 'Necesitas una cadena asignada para dar de alta';
    if (!canUpdateProduct) {
        return 'El alta registra el precio de contado: necesitas también el permiso de editar productos';
    }
    return undefined;
}

/** El botón se oculta sin `retail.product.create`; el precio del alta pide `retail.product.update`. */
export function NewItemButton({
    retailerId,
    onCreated,
}: {
    retailerId: string | null;
    onCreated: (sku: string) => void;
}) {
    const { canCreateProduct, canUpdateProduct } = useInventoryAbilities();
    const [open, setOpen] = useState(false);
    const [openCount, setOpenCount] = useState(0);
    if (!canCreateProduct) return null;
    const blockedReason = newItemBlockedReason(retailerId, canUpdateProduct);
    return (
        <>
            <NewButton
                disabled={blockedReason !== undefined}
                title={blockedReason}
                onClick={() => {
                    setOpenCount((count) => count + 1);
                    setOpen(true);
                }}
            />
            <NewItemDrawer
                isOpen={open}
                openCount={openCount}
                onClose={() => {
                    setOpen(false);
                }}
                retailerId={retailerId}
                onCreated={onCreated}
            />
        </>
    );
}
